<script setup lang="ts">
import AppButton from '@/application/components/ui/AppButton.vue';
import BaseModal from '@/application/components/BaseModal.vue';
import FaIcon from '@/application/components/ui/FaIcon.vue';

interface Props {
  title: string;
  /** Runs on confirm. Shows a spinner on the button for the duration of the promise. */
  confirm: () => unknown;
  icon?: string;
  /** `danger` colors the icon and confirm button red — for delete actions. */
  variant?: 'primary' | 'danger';
  confirmLabel?: string;
  cancelLabel?: string;
  /** Usable height of the bottom sheet on mobile. `auto` (default) fits the content. */
  height?: string;
}

const props = withDefaults(defineProps<Props>(), {
  icon: undefined,
  variant: 'primary',
  confirmLabel: 'OK',
  cancelLabel: 'Abbrechen',
  height: 'auto',
});

const show = defineModel<boolean>('show', { required: true });

async function onConfirm(): Promise<void> {
  await props.confirm();
  show.value = false;
}
</script>

<template>
  <BaseModal
    :show="show"
    :title="title"
    :height="height"
    @close="show = false"
  >
    <div class="flex flex-col gap-6">
      <div class="flex flex-row items-start gap-4 text-sm text-gray-700">
        <div
          v-if="icon"
          class="shrink-0 flex items-center justify-center h-12 w-12 rounded-full"
          :class="variant === 'danger' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'"
        >
          <FaIcon :name="icon" size="lg" variant="regular" fixed-width />
        </div>
        <div class="grow max-w-full">
          <slot />
        </div>
      </div>

      <div class="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
        <AppButton
          @click="show = false"
        >
          {{ cancelLabel }}
        </AppButton>
        <AppButton
          class="ml-0"
          :type="variant"
          :handler="onConfirm"
        >
          {{ confirmLabel }}
        </AppButton>
      </div>
    </div>
  </BaseModal>
</template>
